import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FileSpreadsheet, Search, TrendingDown } from 'lucide-react';

export const ProblemSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const painPoints = [
    {
      icon: FileSpreadsheet,
      title: 'Spreadsheet chaos',
      description:
        "You're tracking hundreds of backlinks across messy Google Sheets. You don't know what's submitted, live, or rejected. Opening the file is a headache.",
      gradient: 'from-red-500 to-orange-500',
    },
    {
      icon: Search,
      title: 'Finding quality sites',
      description:
        'Good free backlink sources are painfully hard to find. Most lists online are outdated, spammy, or built around sites with terrible DA and high spam scores.',
      gradient: 'from-orange-500 to-yellow-500',
    },
    {
      icon: TrendingDown,
      title: 'No ROI visibility',
      description:
        "You have no idea which links went live, which are boosting your DA/PA, or whether any of this effort is actually working.",
      gradient: 'from-yellow-500 to-red-500',
    },
  ];

  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="section-light py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            Link Building Shouldn't Feel Like a{' '}
            <span className="gradient-text">Second Job</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Sound familiar? You're not alone. These are the exact problems that led us to build Searchefy.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {painPoints.map((point, index) => (
            <motion.div
              key={point.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="feature-card group"
              data-testid={`pain-card-${index}`}
            >
              <div
                className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${point.gradient} mb-6`}
              >
                <point.icon size={28} className="text-white" />
              </div>
              <h3 className="font-display text-xl font-bold text-gray-900 mb-3">
                {point.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{point.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
